import { z } from 'zod'
import { cents, MAX_CENTS, negate, sub, ZERO, type Cents } from './money.ts'

/**
 * Registration: who is coming, how many places are left, and who moves up when
 * somebody drops out.
 *
 * The arithmetic is here rather than in the service because "is there room?"
 * and "who is next?" are questions the *client* also has to answer — a form
 * that says "3 places left" and a server that disagrees is worse than a form
 * that says nothing. What is emphatically *not* here is the enforcement: two
 * browsers cannot see each other, so the last place is allocated inside a
 * transaction that holds a lock, exactly like a room booking.
 */

export const REGISTRATION_STATUSES = ['confirmed', 'waitlisted', 'cancelled'] as const
export type RegistrationStatus = (typeof REGISTRATION_STATUSES)[number]

export interface TicketType {
  id: string
  name: string
  /** null ⇒ unlimited. */
  capacity: number | null
  maxPerRegistration: number
  opensAt?: Date | null
  closesAt?: Date | null
}

export interface RegistrationLike {
  id: string
  registrationTypeId: string
  quantity: number
  status: RegistrationStatus
  queuedAt: Date
}

/**
 * Places taken. Only confirmed registrations count.
 *
 * A waitlisted one does not hold a place — that is the whole difference between
 * the two lists, and counting them would keep a full event full forever.
 */
export function placesTaken(registrations: readonly RegistrationLike[], typeId: string): number {
  return registrations
    .filter((entry) => entry.registrationTypeId === typeId && entry.status === 'confirmed')
    .reduce((total, entry) => total + entry.quantity, 0)
}

export interface Availability {
  capacity: number | null
  taken: number
  /** null ⇒ unlimited. Never negative, even if a capacity was lowered later. */
  remaining: number | null
  isFull: boolean
  waitlistLength: number
}

export function availability(
  type: TicketType,
  registrations: readonly RegistrationLike[],
): Availability {
  const taken = placesTaken(registrations, type.id)
  const remaining = type.capacity === null ? null : Math.max(0, type.capacity - taken)
  const waitlistLength = registrations.filter(
    (entry) => entry.registrationTypeId === type.id && entry.status === 'waitlisted',
  ).length

  return {
    capacity: type.capacity,
    taken,
    remaining,
    isFull: remaining !== null && remaining === 0,
    waitlistLength,
  }
}

export type RegistrationOutcome =
  | { kind: 'confirmed' }
  | { kind: 'waitlisted'; position: number }
  | { kind: 'refused'; reason: string }

/**
 * What happens if this person asks for this many places, right now.
 *
 * Partial fills are deliberately refused: a family of four offered two places
 * has to decide something the form cannot decide for them, so they are put on
 * the waitlist whole and told why. Splitting them silently would be worse than
 * either answer.
 */
export function decideRegistration(
  type: TicketType,
  registrations: readonly RegistrationLike[],
  quantity: number,
  now = new Date(),
): RegistrationOutcome {
  if (quantity < 1) return { kind: 'refused', reason: 'Ask for at least one place.' }
  if (quantity > type.maxPerRegistration) {
    return {
      kind: 'refused',
      reason: `Up to ${type.maxPerRegistration} place${type.maxPerRegistration === 1 ? '' : 's'} per booking.`,
    }
  }
  if (type.opensAt && now < type.opensAt) {
    return { kind: 'refused', reason: 'Registration has not opened yet.' }
  }
  if (type.closesAt && now > type.closesAt) {
    return { kind: 'refused', reason: 'Registration has closed.' }
  }

  const state = availability(type, registrations)
  if (state.remaining === null || state.remaining >= quantity) return { kind: 'confirmed' }
  return { kind: 'waitlisted', position: state.waitlistLength + 1 }
}

/**
 * Who moves up when places come free.
 *
 * First come, first served by `queuedAt`, and **skipping** anyone whose party
 * is bigger than the space freed rather than stopping at them. A family of four
 * should not block a single place that the next person could use — but they
 * keep their position, so the next four-place cancellation still finds them at
 * the front.
 */
export function selectForPromotion(
  waiting: readonly RegistrationLike[],
  placesFreed: number,
): RegistrationLike[] {
  const queue = [...waiting]
    .filter((entry) => entry.status === 'waitlisted')
    .sort((a, b) => a.queuedAt.getTime() - b.queuedAt.getTime())

  const promoted: RegistrationLike[] = []
  let left = placesFreed

  for (const entry of queue) {
    if (left <= 0) break
    if (entry.quantity <= left) {
      promoted.push(entry)
      left -= entry.quantity
    }
  }
  return promoted
}

// ---- schemas ---------------------------------------------------------------

export const ticketQuestionSchema = z.object({
  label: z.string().trim().min(1).max(120),
  type: z.enum(['text', 'longtext', 'boolean', 'choice']).default('text'),
  required: z.boolean().default(false),
  options: z.array(z.string().trim().min(1).max(80)).max(20).optional(),
})
export type TicketQuestion = z.infer<typeof ticketQuestionSchema>

export const createTicketTypeSchema = z.object({
  name: z.string().trim().min(1, 'Name the ticket').max(80),
  description: z
    .string()
    .trim()
    .max(500)
    .nullish()
    .transform((v) => (v === '' || v === undefined ? null : v)),
  capacity: z.number().int().min(1).max(100_000).nullish(),
  /** Integer cents. Free until Phase 6; the field exists so it need not move. */
  priceCents: z.number().int().min(0).max(10_000_000).default(0),
  maxPerRegistration: z.number().int().min(1).max(50).default(10),
  opensAt: z.iso.datetime().nullish(),
  closesAt: z.iso.datetime().nullish(),
  questions: z.array(ticketQuestionSchema).max(20).default([]),
})
export type CreateTicketTypeInput = z.infer<typeof createTicketTypeSchema>
export type CreateTicketTypeFormInput = z.input<typeof createTicketTypeSchema>

export const registerSchema = z.object({
  registrationTypeId: z.uuid('Choose a ticket'),
  contactName: z.string().trim().min(1, 'Who is booking?').max(120),
  contactEmail: z.email('That does not look like an email address').nullish(),
  contactPhone: z.string().trim().max(40).nullish(),
  personId: z.uuid().nullish(),
  quantity: z.number().int().min(1).max(50).default(1),
  notes: z
    .string()
    .trim()
    .max(1000)
    .nullish()
    .transform((v) => (v === '' || v === undefined ? null : v)),
  attendees: z
    .array(
      z.object({
        name: z.string().trim().min(1, 'Every place needs a name').max(120),
        personId: z.uuid().nullish(),
        answers: z.record(z.string().max(120), z.unknown()).optional(),
      }),
    )
    .max(50)
    .default([]),
})
export type RegisterInput = z.infer<typeof registerSchema>
export type RegisterFormInput = z.input<typeof registerSchema>

// ---- the list you print on the morning ---------------------------------------

/**
 * One row per **attendee**, not per registration.
 *
 * A family of four booked under one name is four people through the door, and
 * the sheet a steward ticks off has four lines on it. The contact details
 * repeat across those lines on purpose: whoever is holding the clipboard needs
 * the phone number next to the name they are looking at, not on a different
 * row.
 *
 * A registration nobody named individually still gets a line — under the
 * booking name, numbered — because "Okafor, place 2 of 3" is what the steward
 * can actually check off, and an export that silently omitted those places
 * would undercount the room.
 */
export interface AttendeeExportRegistration {
  ticketName: string
  contactName: string
  contactEmail: string | null
  contactPhone: string | null
  quantity: number
  status: RegistrationStatus
  queuedAt: string
  notes: string | null
  attendees: { name: string; answers: unknown }[]
}

const ATTENDEE_EXPORT_BASE = [
  'Attendee',
  'Ticket',
  'Status',
  'Booked by',
  'Email',
  'Phone',
  'Places in booking',
  'Registered',
  'Notes',
] as const

/**
 * The fixed columns, then one per question anybody was asked.
 *
 * Questions are per ticket, so the union across the whole date is what the
 * sheet needs — an "Adult" row simply has nothing under "Child's age". Order
 * follows first appearance rather than the alphabet, so the columns come out
 * in the order somebody set the forms up.
 *
 * Returns the deduplicated question list alongside the header, because that
 * list — not the raw labels the caller passed in — is what `attendeeExportRows`
 * must be given. Hand it the raw ones after a blank or a duplicate was dropped
 * and every answer lands a cell to the left of its heading.
 */
export function attendeeExportColumns(questionLabels: readonly string[]): {
  columns: string[]
  questions: string[]
} {
  const seen = new Set<string>()
  const questions: string[] = []
  for (const label of questionLabels) {
    const trimmed = label.trim()
    if (trimmed === '' || seen.has(trimmed)) continue
    seen.add(trimmed)
    questions.push(trimmed)
  }
  return { columns: [...ATTENDEE_EXPORT_BASE, ...questions], questions }
}

/** An answer as a cell: objects and arrays flattened rather than "[object Object]". */
function cell(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (Array.isArray(value)) return value.map(cell).filter(Boolean).join('; ')
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

export function attendeeExportRows(
  registration: AttendeeExportRegistration,
  questionLabels: readonly string[],
): string[][] {
  const named = registration.attendees
  const lines =
    named.length > 0
      ? named.map((attendee) => ({ name: attendee.name, answers: attendee.answers }))
      : // Nobody was named. One line per place, so the count on the sheet
        // matches the count in the hall.
        Array.from({ length: registration.quantity }, (_, index) => ({
          name:
            registration.quantity === 1
              ? registration.contactName
              : `${registration.contactName} (place ${index + 1} of ${registration.quantity})`,
          answers: undefined as unknown,
        }))

  return lines.map((line) => {
    const answers = (line.answers ?? {}) as Record<string, unknown>
    return [
      line.name,
      registration.ticketName,
      registration.status,
      registration.contactName,
      registration.contactEmail ?? '',
      registration.contactPhone ?? '',
      String(registration.quantity),
      registration.queuedAt.slice(0, 10),
      registration.notes ?? '',
      ...questionLabels.map((label) => cell(answers[label])),
    ]
  })
}

// ---- what a registration cost, and what is still owed ------------------------

/** The cost of a registration when it is made: the ticket's price for each place. */
export const registrationCost = (priceCents: Cents, quantity: number): Cents =>
  cents(priceCents * quantity)

export interface RegistrationBalance {
  cost: Cents
  paid: Cents
  /** Still to pay; zero once paid in full or cancelled. */
  due: Cents
  /** Paid beyond the cost — a refund owed, or a cancellation paid for. */
  credit: Cents
}

/**
 * Cost, paid and due — Planning Center's three numbers. A cancelled place owes
 * nothing, and anything paid towards it becomes a credit for somebody to
 * refund.
 */
export function registrationBalance(input: {
  costCents: Cents
  paidCents: Cents
  status: RegistrationStatus
}): RegistrationBalance {
  const cost = input.status === 'cancelled' ? ZERO : input.costCents
  const owed = sub(cost, input.paidCents)
  return {
    cost: input.costCents,
    paid: input.paidCents,
    due: owed > 0 ? owed : ZERO,
    credit: owed < 0 ? negate(owed) : ZERO,
  }
}

export const recordPaymentSchema = z.object({
  /** The total paid so far, not an increment: setting it twice is harmless. */
  paidCents: z
    .number()
    .int('Amounts are whole cents')
    .min(0, 'Paid cannot be negative')
    .max(MAX_CENTS)
    .transform((value) => cents(value)),
})
